import React, { type ErrorInfo, type ReactNode } from 'react';

/**
 * Last-resort render guard.
 *
 * React unmounts the whole tree when a render throws, so without a boundary any
 * single bad screen renders as a blank white page with no way back — which is
 * exactly how a server/client contract drift on /bo/queue took down the entire
 * admin app. This keeps the failure local and recoverable.
 *
 * It is a safety net, not a licence to skip defensive parsing at the fetch site.
 *
 * `resetKey` exists because a boundary that never clears is its own outage: once
 * one screen threw, every later client-side navigation kept rendering the stale
 * error page, so 22 perfectly healthy screens looked broken. Pass the current
 * pathname and navigating away is enough to recover.
 */
interface Props {
  readonly children: ReactNode;
  readonly resetKey?: string;
}

interface State {
  readonly error: Error | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidUpdate(prevProps: Props): void {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Keep the stack in the console: a blank screen with a silent boundary is
    // barely better than a blank screen.
    console.error('[ErrorBoundary] render failed', error, info.componentStack);
  }

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <section aria-label="app-error" style={{ padding: '2rem' }}>
        <h1>Something went wrong on this screen.</h1>
        <p role="alert">{error.message}</p>
        <button type="button" onClick={() => { this.setState({ error: null }); }}>
          Try again
        </button>
      </section>
    );
  }
}
